/**
 * Phoenix Software — site behaviour.
 *
 * Bundled to wwwroot/assets/js/site.min.js by `npm run build`.
 * Depends on the globals published by vendor.min.js: bootstrap, Swiper, AOS.
 * (PureCounter's "vanilla" build initialises itself on load.)
 */
(() => {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------
   * Header: compact + opaque once the page scrolls.
   * ---------------------------------------------------------------- */
  const header = document.querySelector('#header');

  if (header) {
    const SCROLL_THRESHOLD = 80;
    const syncHeader = () => {
      header.classList.toggle('header-scrolled', window.scrollY > SCROLL_THRESHOLD);
    };

    syncHeader();
    window.addEventListener('scroll', syncHeader, { passive: true });
  }

  /* ----------------------------------------------------------------
   * Back-to-top button.
   * ---------------------------------------------------------------- */
  const backToTop = document.querySelector('.back-to-top');

  if (backToTop) {
    const syncBackToTop = () => {
      backToTop.classList.toggle('active', window.scrollY > 240);
    };

    syncBackToTop();
    window.addEventListener('scroll', syncBackToTop, { passive: true });

    backToTop.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    });
  }

  /* ----------------------------------------------------------------
   * Mobile navigation panel.
   * ---------------------------------------------------------------- */
  const navbar = document.querySelector('#navbar');
  const navToggle = document.querySelector('.mobile-nav-toggle');

  if (navbar && navToggle) {
    const mobileNavQuery = window.matchMedia('(max-width: 991.98px)');
    const toggleLabel = navToggle.querySelector('.mobile-nav-toggle__label');
    const backdrop = document.createElement('div');
    backdrop.className = 'mobile-nav-backdrop';
    backdrop.setAttribute('aria-hidden', 'true');
    document.body.append(backdrop);

    const isNavOpen = () => navbar.classList.contains('navbar-mobile');

    const setNavOpen = (open, returnFocus = false) => {
      const shouldOpen = open && mobileNavQuery.matches;

      navbar.classList.toggle('navbar-mobile', shouldOpen);
      backdrop.classList.toggle('active', shouldOpen);
      document.body.classList.toggle('mobile-nav-open', shouldOpen);
      navToggle.setAttribute('aria-expanded', String(shouldOpen));
      navToggle.setAttribute('aria-label', shouldOpen ? 'Close menu' : 'Open menu');
      navbar.toggleAttribute('aria-hidden', mobileNavQuery.matches && !shouldOpen);
      if (toggleLabel) toggleLabel.textContent = shouldOpen ? 'Close' : 'Menu';
      navToggle.querySelector('i')?.classList.toggle('bi-list', !shouldOpen);
      navToggle.querySelector('i')?.classList.toggle('bi-x-lg', shouldOpen);

      if (shouldOpen) {
        // Put keyboard users directly on the current destination (or first link).
        window.requestAnimationFrame(() => {
          (navbar.querySelector('a[aria-current="page"]') || navbar.querySelector('a'))?.focus();
        });
      } else if (returnFocus) {
        navToggle.focus();
      }
    };

    const closeNav = (returnFocus = false) => setNavOpen(false, returnFocus);

    navToggle.addEventListener('click', () => {
      setNavOpen(!isNavOpen(), isNavOpen());
    });

    backdrop.addEventListener('click', () => closeNav(true));
    navbar.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => closeNav()));

    document.addEventListener('keydown', (event) => {
      if (!isNavOpen()) return;

      if (event.key === 'Escape') {
        closeNav(true);
        return;
      }

      // Keep focus within the open navigation sheet.
      if (event.key === 'Tab') {
        const focusable = [...navbar.querySelectorAll('a'), navToggle];
        const first = focusable[0];
        const last = focusable.at(-1);

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    // Leaving the mobile breakpoint with the panel open would otherwise
    // strand the scroll lock.
    mobileNavQuery.addEventListener('change', (event) => {
      if (!event.matches) closeNav();
      navbar.toggleAttribute('aria-hidden', event.matches && !isNavOpen());
    });

    navbar.toggleAttribute('aria-hidden', mobileNavQuery.matches);
  }

  /* ----------------------------------------------------------------
   * Mark the nav link matching the current page.
   * ---------------------------------------------------------------- */
  const normalisePath = (value) => value.toLowerCase().replace(/\/+$/, '') || '/';
  const currentPath = normalisePath(window.location.pathname);

  document.querySelectorAll('#navbar .primary-nav-link').forEach((link) => {
    const linkPath = normalisePath(new URL(link.href, window.location.origin).pathname);
    const isActive = linkPath === currentPath;

    link.classList.toggle('active', isActive);
    if (isActive) {
      link.setAttribute('aria-current', 'page');
    } else {
      link.removeAttribute('aria-current');
    }
  });

  /* ----------------------------------------------------------------
   * "See more" / "See less" toggles on the product cards.
   *
   * Driven by Bootstrap's collapse events rather than the click, so the
   * label always matches the panel even when it is toggled another way.
   * ---------------------------------------------------------------- */
  document.querySelectorAll('.view-btn').forEach((button) => {
    const target = document.querySelector(button.getAttribute('href') || button.dataset.bsTarget);
    if (!target) return;

    const label = button.querySelector('.view-btn__label') ?? button;
    const setLabel = (expanded) => {
      label.textContent = expanded ? 'See less' : 'See more';
    };

    setLabel(target.classList.contains('show'));
    target.addEventListener('show.bs.collapse', () => setLabel(true));
    target.addEventListener('hide.bs.collapse', () => setLabel(false));
  });

  /* ----------------------------------------------------------------
   * Testimonials carousel.
   * ---------------------------------------------------------------- */
  const testimonials = document.querySelector('.testimonials-swiper');

  if (testimonials && typeof window.Swiper === 'function') {
    new window.Swiper(testimonials, {
      speed: 500,
      loop: true,
      autoHeight: true,
      spaceBetween: 24,
      autoplay: prefersReducedMotion ? false : { delay: 6000, disableOnInteraction: true },
      pagination: { el: '.swiper-pagination', type: 'bullets', clickable: true },
      a11y: { enabled: true },
      breakpoints: {
        768: { slidesPerView: 1 },
        992: { slidesPerView: 2 },
      },
    });
  }

  /* ----------------------------------------------------------------
   * Legal pages: responsive table of contents + current section.
   * ---------------------------------------------------------------- */
  const toc = document.querySelector('.legal-toc');

  if (toc) {
    const tocDetails = toc.querySelector('details');
    const tocSummary = toc.querySelector('summary');
    const currentLabel = toc.querySelector('.legal-toc__current');
    const desktopTocQuery = window.matchMedia('(min-width: 992px)');
    const sections = [...toc.querySelectorAll('a[href^="#"]')]
      .map((link) => ({
        link,
        section: document.getElementById(decodeURIComponent(link.hash.slice(1))),
      }))
      .filter(({ section }) => section);

    const setTocMode = (desktop) => {
      if (!tocDetails || !tocSummary) return;
      tocDetails.open = desktop;
      if (desktop) tocSummary.tabIndex = -1;
      else tocSummary.removeAttribute('tabindex');
    };

    setTocMode(desktopTocQuery.matches);
    desktopTocQuery.addEventListener('change', (event) => setTocMode(event.matches));

    for (const { link } of sections) {
      link.addEventListener('click', () => {
        if (desktopTocQuery.matches || !tocDetails || !tocSummary) return;
        tocDetails.open = false;
        tocSummary.focus({ preventScroll: true });
      });
    }

    if (sections.length) {
      let updateRequested = false;

      const updateCurrent = () => {
        updateRequested = false;
        const headerHeight = header?.getBoundingClientRect().height ?? 0;
        const tocHeight = desktopTocQuery.matches
          ? 0
          : (tocSummary?.getBoundingClientRect().height ?? 0);
        const readingLine = headerHeight + tocHeight + 24;
        let active = sections[0];

        for (const candidate of sections) {
          if (candidate.section.getBoundingClientRect().top <= readingLine) active = candidate;
          else break;
        }

        // Ensure the final short section becomes current at the bottom of the page.
        const atPageEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
        if (atPageEnd) active = sections.at(-1);

        for (const candidate of sections) {
          const isCurrent = candidate === active;
          candidate.link.classList.toggle('is-current', isCurrent);
          if (isCurrent) candidate.link.setAttribute('aria-current', 'location');
          else candidate.link.removeAttribute('aria-current');
        }

        if (currentLabel) currentLabel.textContent = active.link.textContent.trim();
      };

      const requestCurrentUpdate = () => {
        if (updateRequested) return;
        updateRequested = true;
        window.requestAnimationFrame(updateCurrent);
      };

      updateCurrent();
      window.addEventListener('scroll', requestCurrentUpdate, { passive: true });
      window.addEventListener('resize', requestCurrentUpdate);
      window.addEventListener('hashchange', requestCurrentUpdate);
    }
  }

  /* ----------------------------------------------------------------
   * Scroll-reveal animations.
   * ---------------------------------------------------------------- */
  if (typeof window.AOS !== 'undefined') {
    if (prefersReducedMotion) {
      window.AOS.init({ disable: true });
    } else {
      window.AOS.init({
        duration: 700,
        easing: 'ease-in-out',
        once: true,
        mirror: false,
        offset: 60,
      });
    }
  }
})();
